import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Upload, Wand2, Play, Pause, Volume2, Brain, BookOpen, Sparkles, Mic, Send, Settings2, Loader2 } from "lucide-react";
// --- Composants UI minimalistes (remplacent shadcn/ui) ---
const Card = ({ className = "", children }) => (
    <div className={`rounded-2xl border bg-white ${className}`}>{children}</div>
);
const CardContent = ({ className = "", children }) => (
    <div className={className}>{children}</div>
);

const Button = ({ variant = "default", className = "", children, ...props }) => {
    const base = "inline-flex items-center justify-center rounded-2xl px-3 py-2 text-sm transition shadow-sm";
    const styles =
        variant === "secondary"
            ? "bg-gray-100 hover:bg-gray-200 text-gray-900"
            : variant === "outline"
                ? "bg-white border hover:bg-gray-50"
                : variant === "ghost"
                    ? "bg-transparent hover:bg-gray-100"
                    : "bg-blue-600 hover:bg-blue-700 text-white";
    return (
        <button className={`${base} ${styles} ${className}`} {...props}>
            {children}
        </button>
    );
};

const Input = ({ className = "", ...props }) => (
    <input
        className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200 ${className}`}
        {...props}
    />
);

const Textarea = ({ className = "", ...props }) => (
    <textarea
        className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200 ${className}`}
        {...props}
    />
);

// Switch simple (checkbox stylée)
const Switch = ({ checked = false, onCheckedChange = () => { } }) => (
    <label className="inline-flex items-center cursor-pointer select-none">
        <input
            type="checkbox"
            className="sr-only"
            checked={checked}
            onChange={(e) => onCheckedChange(e.target.checked)}
        />
        <span
            className={`relative w-11 h-6 rounded-full transition ${checked ? "bg-indigo-600" : "bg-gray-300"
                }`}
        >
            <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${checked ? "translate-x-5" : ""
                    }`}
            />
        </span>
    </label>
);

// Barre de progression simple
const Progress = ({ value = 0 }) => (
    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
            className="h-full bg-indigo-600 transition-all"
            style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        />
    </div>
);


// ------------------------------------------------------
// OlivIA — Démo immersive pour Grand Est Développement
// Front: React + Tailwind + shadcn/ui + Framer Motion
// Simulations: faux scan, analyse matière, explication pas à pas
// Accessibilité: option DYS (phrases simples + police adaptée), TTS local
// Firebase (optionnel): léger, pour logguer les actions si config fournie
// ------------------------------------------------------

// (Optionnel) — Firebase léger
// Remplacez la configuration par la vôtre si vous voulez tracer des événements.
let logEventSafe = (_name, _params) => { };
try {
    // Lazy import pour éviter les erreurs si le build n'a pas Firebase
    // et n'activer que si une config (measurementId) est renseignée.
    // @ts-ignore
    // eslint-disable-next-line
    (async () => {
        const firebaseConfig = {
            // apiKey: "",
            // authDomain: "",
            // projectId: "",
            // storageBucket: "",
            // messagingSenderId: "",
            // appId: "",
            // measurementId: "" // <= si présent, Analytics s'active
        };
        if (firebaseConfig && firebaseConfig.measurementId) {
            const { initializeApp } = await import("firebase/app");
            const { getAnalytics, logEvent } = await import("firebase/analytics");
            const app = initializeApp(firebaseConfig);
            const analytics = getAnalytics(app);
            logEventSafe = (name, params) => logEvent(analytics, name, params);
            logEventSafe("olivia_demo_loaded");
        }
    })();
} catch (e) {
    // pas de Firebase — aucun souci pour la démo locale
}

// Heuristiques simples pour détecter la matière
function detectSubject(text) {
    const t = (text || "").toLowerCase();
    const rules = [
        { key: "maths", match: /(équation|fraction|pgcd|théorème|triangle|périmètre|aire|x\s*[+\-*/]|\balgèbre|dérivée|intégrale)/i },
        { key: "français", match: /(conjugaison|orthographe|grammaire|récit|rédaction|discours|complément|accord)/i },
        { key: "anglais", match: /(translate|anglais|irregular verbs|present perfect|past simple|vocabulary|essay)/i },
        { key: "histoire", match: /(révolution|siècle|empire|guerre|traité|monarchie|antiquité|moyen âge)/i },
        { key: "géographie", match: /(relief|climat|carte|population|paysage|urbanisation|océan|continent)/i },
        { key: "physique-chimie", match: /(expérience|masse|volume|densité|réaction|molécule|énergie|vitesse|force)/i },
        { key: "SVT", match: /(cellule|ADN|écosystème|respiration|digestion|biodiversité|organe|reproduction)/i },
    ];
    for (const r of rules) if (r.match.test(t)) return r.key;
    // fallback: quelques mots-clés basiques
    if (/\bx\b|=|\+|\-|\*|\/|triangle|aire/.test(t)) return "maths";
    if (/\b(anglais|english)\b/.test(t)) return "anglais";
    if (/\b(rédiger|récit|texte)\b/.test(t)) return "français";
    return "à déterminer";
}

// Génération d'un plan d'explication en fonction de la matière
function generatePlan(subject, text) {
    const base = {
        maths: {
            rule: "On identifie les données, on isole l'inconnue, puis on calcule étape par étape.",
            example: "Ex: Résoudre 3x + 5 = 20 → 3x = 15 → x = 5.",
            hint: "Souligne les nombres, repère l'opération et vérifie l'unité.",
        },
        français: {
            rule: "On lit l'énoncé, on repère la consigne (temps, personne), puis on applique la règle.",
            example: "Ex: Passé composé du verbe ‘aller’ (je) → je suis allé(e).",
            hint: "Cherche le sujet, le verbe et les accords.",
        },
        anglais: {
            rule: "On comprend le sens global, on repère le temps verbal, puis on traduit sans calque mot-à-mot.",
            example: "Ex: Present perfect → have/has + Vpp: ‘I have seen this movie’.",
            hint: "Utilise un dictionnaire simple si un mot te bloque.",
        },
        "physique-chimie": {
            rule: "On identifie les grandeurs (unités), on applique la formule et on justifie le résultat.",
            example: "Ex: v = d / t : si d=100 m et t=10 s → v=10 m/s.",
            hint: "Écris la formule avant de remplacer par les nombres.",
        },
        SVT: {
            rule: "On observe, on décrit, puis on explique le phénomène avec le bon vocabulaire.",
            example: "Ex: Le poumon permet les échanges gazeux (O2/CO2).",
            hint: "Fais un petit schéma, même simple.",
        },
        histoire: {
            rule: "On situe dans le temps et l'espace, on nomme les acteurs et on explique les causes/conséquences.",
            example: "Ex: 1789 → Révolution française : fin de la monarchie absolue.",
            hint: "Une frise rapide aide beaucoup.",
        },
        géographie: {
            rule: "On lit la carte, on repère les échelles et on décrit l'organisation de l'espace.",
            example: "Ex: Ville-centre, banlieue, périurbain.",
            hint: "Toujours relier carte ↔ texte.",
        },
    };
    const s = base[subject] || {
        rule: "On clarifie la consigne, on repère les mots-clés, puis on cherche une méthode adaptée.",
        example: "Ex: Reformuler l'énoncé avec tes mots pour vérifier que tu as compris.",
        hint: "Souligne ce que tu dois produire (ex: calcul, texte, schéma).",
    };
    return [
        { type: "step", title: "1) Comprendre l'énoncé", text: `Relis calmement et souligne les verbes d'action. ${s.hint}` },
        { type: "step", title: "2) La règle utile", text: s.rule },
        { type: "step", title: "3) Exemple guidé", text: s.example },
        { type: "step", title: "4) À toi !", text: "Refais la démarche avec ton énoncé. Je reste là si tu bloques." },
    ];
}

// Simplification DYS: phrases plus courtes
function simplifySentence(str) {
    return (str || "")
        .replace(/\bcependant\b/gi, "mais")
        .replace(/\bainsi\b/gi, "donc")
        .replace(/\bnéanmoins\b/gi, "mais")
        .replace(/\bapplication\b/gi, "utilisation")
        .replace(/[,;:]/g, ".")
        .replace(/\s+/g, " ")
        .replace(/\.\s*\./g, ".");
}

// Typewriter effect for OlivIA messages
function useTypewriter(text, speed = 18) {
    const [out, setOut] = useState("");
    useEffect(() => {
        setOut("");
        if (!text) return;
        let i = 0;
        const id = setInterval(() => {
            i++;
            setOut(text.slice(0, i));
            if (i >= text.length) clearInterval(id);
        }, speed);
        return () => clearInterval(id);
    }, [text, speed]);
    return out;
}

function useSpeech() {
    const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
    const [speaking, setSpeaking] = useState(false);
    const [voiceReady, setVoiceReady] = useState(false);

    useEffect(() => {
        if (!synth) return;
        const onVoicesChanged = () => setVoiceReady(true);
        synth.addEventListener?.("voiceschanged", onVoicesChanged);
        setVoiceReady(true);
        return () => synth.removeEventListener?.("voiceschanged", onVoicesChanged);
    }, [synth]);

    const speak = (text, lang = "fr-FR") => {
        if (!synth || !text) return;
        const u = new SpeechSynthesisUtterance(text);
        const voices = synth.getVoices?.() || [];
        u.lang = lang;

        // 🎙️ Réglages de voix (débit/ton) — PAS d'expressions seules !
        u.rate = 0.9;   // plus calme (0.8 = lent, 1.0 = normal)
        u.pitch = 1.1;  // un peu plus doux/jeune

        // Choix de la voix FR
        const fr =
            voices.find(v => v.name === "Marie") ||
            voices.find(v => v.name === "Sandy (français (France))") ||
            voices.find(v => v.name === "Google français") ||
            voices.find(v => v.lang?.startsWith("fr"));
        if (fr) u.voice = fr;

        u.onend = () => setSpeaking(false);
        synth.cancel();
        synth.speak(u);
        setSpeaking(true);
    };

    const stop = () => {
        if (!synth) return;
        synth.cancel();
        setSpeaking(false);
    };

    return { speak, stop, speaking, voiceReady };
}


// Message Bubble
function Bubble({ role, children }) {
    const isAI = role !== "user";
    return (
        <div className={`flex ${isAI ? "justify-start" : "justify-end"} w-full my-2`}>
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`max-w-[82%] rounded-2xl px-4 py-3 shadow-sm ${isAI ? "bg-white/90 border border-gray-100" : "bg-blue-600 text-white"
                    }`}
            >
                {children}
            </motion.div>
        </div>
    );
}

export default function OliviaDemo() {
    const [image, setImage] = useState(null);
    const [pasted, setPasted] = useState("");
    const [progress, setProgress] = useState(0);
    const [subject, setSubject] = useState("—");
    const [steps, setSteps] = useState([]);
    const [analyzing, setAnalyzing] = useState(false);
    const [messages, setMessages] = useState([
        { role: "assistant", text: "Coucou, je suis OlivIA 💫 Ta camarade numérique pour t'aider à faire tes devoirs à la maison. Tu peux me montrer ton cahier (photo) ou coller le texte de l'exercice ici." }
    ]);
    const [userInput, setUserInput] = useState("");
    const [dysMode, setDysMode] = useState(false);
    const [autoRead, setAutoRead] = useState(true);
    const [simpleSentences, setSimpleSentences] = useState(true);
    const { speak, stop, speaking, voiceReady } = useSpeech();
    const endRef = useRef(null);
    const [demoMode, setDemoMode] = useState(false);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, analyzing]);

    // Police adaptée DYS
    useEffect(() => {
        const id = "olivia-dys-style";
        if (dysMode) {
            const style = document.createElement("style");
            style.id = id;
            style.innerHTML = `
  .dys-font { font-family: 'OpenDyslexic', system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; letter-spacing: 0.02em; line-height: 1.6; }
  .dys-input { font-size: 1.05rem; }
`;
            document.head.appendChild(style);
        } else {
            document.getElementById(id)?.remove();
        }
    }, [dysMode]);

    const aiSay = (text) => {
        const out = simpleSentences ? simplifySentence(text) : text;
        setMessages(m => [...m, { role: "assistant", text: out }]);
        if (autoRead) speak(out);
    };

    const onSend = () => {
        const t = userInput.trim();
        if (!t) return;
        setMessages(m => [...m, { role: "user", text: t }]);
        setUserInput("");
        logEventSafe("user_message", { len: t.length });

        // Réponse courte et bienveillante simulée
        const reply = `Merci ! Je t'accompagne pas à pas. Peux-tu me dire où tu bloques exactement ? (ex: la règle, un calcul, une phrase à écrire…)`;
        setTimeout(() => aiSay(reply), 300);
    };

    const simulateScan = (srcKind) => {
        setAnalyzing(true);
        setProgress(0);
        logEventSafe("scan_started", { src: srcKind });

        const tick = () => {
            setProgress(p => {
                if (p >= 100) return 100;
                return p + Math.floor(8 + Math.random() * 12);
            });
        };

        const id = setInterval(tick, 180);
        setTimeout(() => {
            clearInterval(id);
            setProgress(100);
            // Détection matière
            const text = pasted;
            const subj = detectSubject(text);
            setSubject(subj);
            const plan = generatePlan(subj, text);
            setSteps(plan);
            setAnalyzing(false);
            logEventSafe("scan_done", { subj });

            aiSay(`J'ai reconnu: ${subj === "—" ? "matière à déterminer" : subj}. On y va tranquillement, étape par étape.`);
        }, 2200 + Math.random() * 900);
    };

    const handleImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        setImage(url);
        simulateScan("image");
    };

    const handlePasteAnalyze = () => {
        if (!pasted.trim()) {
            aiSay("Colle d'abord ton énoncé, s'il te plaît 😊");
            return;
        }
        simulateScan("text");
    };

    const currentPlan = useMemo(() => {
        return steps.map(s => ({ ...s, text: dysMode && simpleSentences ? simplifySentence(s.text) : s.text }));
    }, [steps, dysMode, simpleSentences]);

    const lastAssistant = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === "assistant") return messages[i].text;
        return "";
    }, [messages]);
    const typed = useTypewriter(lastAssistant, 14);

    return (
        <div
  className={`min-h-screen ${dysMode ? "dys-font" : ""} bg-gradient-to-b from-indigo-200 via-white to-indigo-100 flex flex-col items-center ${demoMode ? "scale-[1.03]" : ""}`}
>
            <div className="max-w-5xl mx-auto px-4 py-8">
               <header className="flex items-center justify-between mb-6 rounded-2xl px-4 py-3 bg-white/70 backdrop-blur border shadow-sm">
  {/* Groupe gauche : logo + titre */}
  <div className="flex items-center gap-3">
    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="p-2 rounded-2xl bg-white/80 shadow-sm">
      <img src="/logo-olivia.png" alt="Logo OlivIA" className="w-16 h-16 object-contain" />
    </motion.div>
    <div>
      <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">OlivIA — Démo immersive</h1>
      <p className="text-sm text-gray-600">Camarade numérique bienveillante • Aide aux devoirs à la maison</p>
    </div>
  </div>

  {/* Groupe droite : les 4 switchs dans UN SEUL conteneur */}
  <div className="flex items-center gap-4">
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600">Mode DYS</span>
      <Switch checked={dysMode} onCheckedChange={setDysMode} />
    </div>

    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600">Phrases simples</span>
      <Switch checked={simpleSentences} onCheckedChange={setSimpleSentences} />
    </div>

    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600">Lecture auto</span>
      <Switch checked={autoRead} onCheckedChange={setAutoRead} />
    </div>

    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600">Mode démo</span>
      <Switch checked={demoMode} onCheckedChange={setDemoMode} />
    </div>
  </div>
</header>

                {/* Zone d'entrée: scan fictif */}
                <Card className="border-none shadow-md mb-6">
                    <CardContent className="p-4 md:p-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <p className="text-sm text-gray-600 mb-2">1) Montre ton devoir</p>
                                <div className="flex gap-2">
                                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white cursor-pointer hover:bg-gray-50">
                                        <Camera className="w-4 h-4" />
                                        <span>Photo du cahier</span>
                                        <input type="file" accept="image/*" onChange={handleImage} className="hidden" />
                                    </label>
                                    <Button variant="secondary" onClick={() => document.getElementById("pasted")?.focus()}>
                                        <Upload className="w-4 h-4 mr-2" /> Coller le texte
                                    </Button>
                                </div>
                                {image && (
                                    <div className="mt-3">
                                        <img src={image} alt="Cahier" className="rounded-xl max-h-48 object-cover border" />
                                    </div>
                                )}
                            </div>

                            <div>
                                <p className="text-sm text-gray-600 mb-2">2) Ou colle l'énoncé ici</p>
                                <Textarea id="pasted" value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder="Ex: Résoudre l'équation 3x + 5 = 20. Expliquer la méthode…" className={`min-h-28 ${dysMode ? "dys-input" : ""}`} />
                                <div className="flex gap-2 mt-3">
                                    <Button onClick={handlePasteAnalyze} disabled={analyzing}>
                                        <Wand2 className="w-4 h-4 mr-2" /> Analyser
                                    </Button>
                                    <Button variant="outline" onClick={() => { setPasted(""); setImage(null); setSubject("—"); setSteps([]); }}>
                                        Réinitialiser
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {analyzing && (
                            <div className="mt-6">
                                <div className="flex items-center gap-2 text-indigo-700 mb-2"><Loader2 className="w-4 h-4 animate-spin" /> Analyse en cours…</div>
                                <Progress value={progress} />
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Résultat d'analyse */}
                {steps.length > 0 && (
                    <Card className="border-none shadow-md mb-6">
                        <CardContent className="p-4 md:p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Brain className="w-5 h-5 text-indigo-600" />
                                    <h2 className="text-lg font-semibold">Matière reconnue: <span className="text-indigo-700">{subject}</span></h2>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button variant="ghost" onClick={() => speak(`Matière reconnue ${subject}. Je t'explique pas à pas.`)} disabled={!voiceReady}>
                                        <Volume2 className="w-4 h-4 mr-2" /> Lire
                                    </Button>
                                    {speaking ? (
                                        <Button variant="outline" onClick={stop}><Pause className="w-4 h-4 mr-2" />Stop</Button>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4 mt-4">
                                {currentPlan.map((s, i) => (
                                    <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="rounded-2xl p-4 border bg-white">
                                        <div className="flex items-center gap-2 mb-2">
                                            <BookOpen className="w-4 h-4 text-indigo-600" />
                                            <span className="font-medium">{s.title}</span>
                                        </div>
                                        <p className="text-gray-700 leading-relaxed">{s.text}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Interface chat bienveillante */}
                <Card className="border-none shadow-md">
                    <CardContent className="p-0 md:p-2">
                        <div className="h-[360px] overflow-y-auto p-4">
                            {messages.map((m, idx) => (
                                <Bubble role={m.role} key={idx}>
                                    {m.role === "assistant" && idx === messages.length - 1 ? typed : m.text}
                                </Bubble>
                            ))}
                            <div ref={endRef} />
                        </div>
                        <div className="border-t p-3 flex items-center gap-2">
                            <Input
                                value={userInput}
                                onChange={e => setUserInput(e.target.value)}
                                placeholder="Écris-moi où tu bloques…"
                                className={dysMode ? "dys-input" : ""}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
                            />
                            <Button onClick={onSend}><Send className="w-4 h-4 mr-1" />Envoyer</Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Bandeau final */}
                <div className="text-center text-xs text-gray-500 mt-6">
                    Démo conceptuelle — pas destinée à un usage scolaire en classe. Conçue pour illustrer l'expérience à la maison.
                </div>
            </div>
        </div>
    );
}
